/* LAV TDMS — DS primitives, faithfully ported from the prototype's _ds_bundle
   (inline token-based styles, sharp corners, hairline borders). Export names
   kept stable so screens adopt the exact prototype styling unchanged. */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { Icon } from '@/ds/icons'
import type { LifeStatus, Purpose as PurposeT, ReviewCode } from '@/data/types'
import { useCodeMeanings } from '@/data/meta'
import { useNarrow } from './responsive'
import './ui.css'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'dark' | 'default' | 'subtle'
type Size = 'sm' | 'md' | 'lg'

const BTN_H: Record<Size, string> = { sm: 'var(--control-sm)', md: 'var(--control-md)', lg: 'var(--control-lg)' }
const BTN_PAD: Record<Size, string> = { sm: '0 10px', md: '0 14px', lg: '0 18px' }
const BTN_FS: Record<Size, string> = { sm: 'var(--text-xs)', md: 'var(--text-md)', lg: 'var(--text-md)' }
const BTN_VAR: Record<string, CSSProperties> = {
  primary: { background: 'var(--accent)', color: 'var(--on-accent)', borderColor: 'var(--accent)' },
  secondary: { background: 'var(--surface-card)', color: 'var(--text-body)', borderColor: 'var(--border-strong)' },
  ghost: { background: 'transparent', color: 'var(--text-muted)', borderColor: 'transparent' },
  danger: { background: 'var(--surface-card)', color: 'var(--danger-ink)', borderColor: 'var(--danger-line)' },
  dark: { background: 'var(--slate-900)', color: 'var(--slate-100)', borderColor: 'var(--slate-900)' },
}
const variantOf = (v: Variant): keyof typeof BTN_VAR => (v === 'default' || v === 'subtle' ? 'secondary' : v)

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconLeft,
  iconRight,
  block = false,
  disabled = false,
  type = 'button',
  style,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  icon?: string
  iconLeft?: ReactNode
  iconRight?: ReactNode
  block?: boolean
}) {
  const v = variantOf(variant)
  const left = iconLeft ?? (icon ? <Icon name={icon} size={size === 'sm' ? 13 : 15} /> : null)
  return (
    <button
      type={type}
      disabled={disabled}
      className={`lav-btn lav-btn-${v}`}
      style={{
        display: block ? 'flex' : 'inline-flex',
        width: block ? '100%' : undefined,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        height: BTN_H[size],
        padding: BTN_PAD[size],
        font: 'var(--font-ui)',
        fontSize: BTN_FS[size],
        fontWeight: 'var(--weight-medium)',
        lineHeight: 1,
        border: 'var(--border-hairline) solid transparent',
        borderRadius: 'var(--radius-0)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        // transform обязан быть в списке: инлайновый transition заменяет
        // свойство целиком, и без него нажатие кнопки DS срабатывало мгновенно
        // и так же мгновенно отпускало — при том что ui.css эту анимацию для
        // .lav-btn-* задаёт. У цветовых свойств кривая из токена обязательна:
        // без неё браузер подставляет свой ease.
        transition: 'transform var(--duration-press) var(--ease-out), background var(--duration-fast) var(--ease-standard), border-color var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard)',
        ...BTN_VAR[v],
        ...style,
      }}
      {...rest}
    >
      {left && <span style={{ display: 'inline-flex', flex: '0 0 auto' }}>{left}</span>}
      {children != null && <span>{children}</span>}
      {iconRight && <span style={{ display: 'inline-flex', flex: '0 0 auto' }}>{iconRight}</span>}
    </button>
  )
}

/* Живой индикатор загрузки: единый способ для всех «Загрузка…» по экранам —
   иначе одни места мигают колесом, другие голым текстом. Вид и ветку
   reduced-motion несёт класс .spinner из ui.css, компонент добавляет только
   размер. inline-block — потому что у строчного span ширина и высота не
   работают; flex: 0 0 auto — чтобы кружок не сплющивало рядом с текстом. */
export function Spinner({ size = 14 }: { size?: number }) {
  return <span className="spinner" style={{ display: 'inline-block', flex: '0 0 auto', width: size, height: size }} aria-hidden />
}


export type Tone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent'
const TONES: Record<Tone, { ink: string; bg: string; line: string; dot: string }> = {
  neutral: { ink: '--text-muted', bg: '--surface-sunken', line: '--border-default', dot: '--slate-400' },
  info: { ink: '--info-ink', bg: '--info-bg', line: '--info-line', dot: '--blue-500' },
  success: { ink: '--success-ink', bg: '--success-bg', line: '--success-line', dot: '--green-500' },
  warning: { ink: '--warning-ink', bg: '--warning-bg', line: '--warning-line', dot: '--yellow-500' },
  danger: { ink: '--danger-ink', bg: '--danger-bg', line: '--danger-line', dot: '--red-500' },
  accent: { ink: '--accent-ink', bg: '--accent-soft', line: '--accent-line', dot: '--amber-400' },
}
export function Badge({
  tone = 'neutral',
  dot = false,
  size = 'md',
  style,
  children,
}: {
  tone?: Tone
  dot?: boolean
  size?: 'sm' | 'md'
  style?: CSSProperties
  children: ReactNode
}) {
  const t = TONES[tone]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        height: size === 'sm' ? '18px' : '20px',
        padding: '0 var(--space-4)',
        font: 'var(--font-ui)',
        fontSize: size === 'sm' ? 'var(--text-2xs)' : 'var(--text-xs)',
        fontWeight: 'var(--weight-medium)',
        color: `var(${t.ink})`,
        background: dot ? 'transparent' : `var(${t.bg})`,
        border: `var(--border-hairline) solid ${dot ? 'var(--border-default)' : `var(${t.line})`}`,
        borderRadius: 'var(--radius-sm)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: 'var(--radius-pill)', background: `var(${t.dot})`, flex: '0 0 auto' }} />}
      {children}
    </span>
  )
}

const STATUS: Record<string, { ink: string; bg: string; line: string }> = {
  IFR: { ink: '--status-ifr-ink', bg: '--status-ifr-bg', line: '--status-ifr-line' },
  IFA: { ink: '--status-ifa-ink', bg: '--status-ifa-bg', line: '--status-ifa-line' },
  AFD: { ink: '--status-afd-ink', bg: '--status-afd-bg', line: '--status-afd-line' },
  IFC: { ink: '--status-ifc-ink', bg: '--status-ifc-bg', line: '--status-ifc-line' },
}
export function StatusBadge({ status, size = 'md' }: { status: string; size?: 'sm' | 'md' }) {
  const s = STATUS[status] ?? STATUS.IFR!
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        /* Ширина — по надписи. Прямого потомка сетки браузер растягивает на
           всю колонку, и закрашенное поле фишки упиралось в соседнюю дату:
           в строке текста fit-content ничего не меняет, а в сетке возвращает
           фишке её собственный размер. */
        width: 'fit-content',
        height: size === 'sm' ? '18px' : '20px',
        padding: '0 var(--space-4)',
        fontFamily: 'var(--font-mono)',
        fontSize: size === 'sm' ? '10px' : 'var(--text-2xs)',
        fontWeight: 'var(--weight-semibold)',
        letterSpacing: '0.04em',
        color: `var(${s.ink})`,
        background: `var(${s.bg})`,
        border: `var(--border-hairline) solid var(${s.line})`,
        borderRadius: 'var(--radius-sm)',
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  )
}

/* Цвет фишки — по СМЫСЛУ кода, а не по букве: у одного заказчика коды A/B/C,
   у другого 1/2/3/4, и зелёным обязан быть тот, что означает «принято» (V71).
   Смысл берётся из того же справочника, что показан на экране справочников. */
const MEANING_COLOR: Record<string, string> = { afd: '--code-1', approved: '--code-2', reissue: '--code-3' }
export function CodeChip({ code, size = 'md' }: { code: ReviewCode | null; size?: 'sm' | 'md' }) {
  const meanings = useCodeMeanings()
  if (!code) return <span style={{ color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>—</span>
  const meaning = meanings[code]
  const box = size === 'sm' ? 16 : 18
  return (
    <span
      style={{
        display: 'inline-grid',
        placeItems: 'center',
        width: box,
        height: box,
        flex: '0 0 auto',
        background: `var(${MEANING_COLOR[meaning ?? ''] ?? '--code-4'})`,
        color: meaning === 'reissue' ? 'var(--slate-900)' : '#fff',
        fontFamily: 'var(--font-mono)',
        fontSize: size === 'sm' ? '10px' : '11px',
        fontWeight: 600,
        borderRadius: 'var(--radius-sm)',
        lineHeight: 1,
      }}
    >
      {code}
    </span>
  )
}

const DATE_KIND: Record<string, string> = {
  planned: '--date-planned',
  forecast: '--date-forecast',
  actual: '--date-actual',
  overdue: '--date-overdue',
  default: '--text-muted',
}
export function DateCell({ value, kind = 'default', strong = false, title }: { value?: string | null; kind?: string; strong?: boolean
                                                                                  /** К чему относится дата: стадия круга у сроков реестра. */
                                                                                  title?: string }) {
  const empty = value == null || value === '' || value === '—'
  return (
    <span
      title={title}
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--text-xs)',
        fontFeatureSettings: '"tnum" 1',
        color: empty ? 'var(--text-subtle)' : `var(${DATE_KIND[kind] ?? DATE_KIND.default})`,
        fontWeight: strong && !empty ? 'var(--weight-semibold)' : 'var(--weight-regular)',
        whiteSpace: 'nowrap',
      }}
    >
      {empty ? '—' : value}
    </span>
  )
}


export function Card({
  title,
  subtitle,
  actions,
  padding = true,
  raised = false,
  style,
  bodyStyle,
  children,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  padding?: boolean
  raised?: boolean
  style?: CSSProperties
  bodyStyle?: CSSProperties
  children: ReactNode
}) {
  return (
    <section
      style={{
        background: 'var(--surface-card)',
        border: 'var(--border-hairline) solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        boxShadow: raised ? 'var(--shadow-sm)' : 'var(--shadow-none)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {(title || actions) && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-6)',
            padding: 'var(--space-6) var(--space-8)',
            borderBottom: 'var(--border-hairline) solid var(--border-subtle)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            {title && <h3 style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{title}</h3>}
            {subtitle && <div style={{ marginTop: 'var(--space-1)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{subtitle}</div>}
          </div>
          {actions && <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>{actions}</div>}
        </header>
      )}
      <div style={{ padding: padding ? 'var(--space-8)' : 0, ...bodyStyle }}>{children}</div>
    </section>
  )
}

export function Panel({ children, pad, className, style }: { children: ReactNode; pad?: boolean; className?: string; style?: CSSProperties }) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--surface-card)',
        border: 'var(--border-hairline) solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        padding: pad ? 'var(--space-12)' : 0,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

export function PageHead({
  crumbs,
  title,
  sub,
  count,
  actions,
  tabsNode,
  sticky = true,
}: {
  crumbs?: ReactNode
  title: ReactNode
  sub?: ReactNode
  count?: number | string | null
  actions?: ReactNode
  tabsNode?: ReactNode
  sticky?: boolean
}) {
  /* Экран занимает высоту окна, а прокручивается его рабочая область — поэтому
     шапка стоит на месте сама, без липкости, и всё, что она съедает, забирается
     у таблицы. На 14 дюймах под данные оставалось четыре строки из сорока
     шести, и первое, что здесь лишнее, — хлебные крошки: они повторяют
     заголовок и стоят строкой в 18 px. */
  const narrow = useNarrow()
  return (
    <div
      style={{
        position: sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 5,
        flex: '0 0 auto',
        padding: narrow ? '10px 24px 0' : '18px 24px 0',
        background: 'var(--surface-card)',
        borderBottom: tabsNode ? 'none' : '1px solid var(--border-default)',
      }}
    >
      {crumbs && !narrow && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 6 }}>{crumbs}</div>
      )}
      {/* Заголовок и кнопки в один ряд не помещаются: на реестре MDR в actions
          стоит семь элементов, около 1090 px вместе с заголовком, а на
          ноутбуке под шапку отдано 1094 и меньше. Без minWidth заголовок не
          сжимается вовсе (min-width: auto у элемента flex), и ряд вылезает за
          край; без переноса кнопки уезжают под правую границу. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: tabsNode ? 12 : narrow ? 10 : 16, flexWrap: 'wrap' }}>
        <h1 style={{ font: 'var(--font-page-title)', color: 'var(--text-strong)', letterSpacing: 'var(--tracking-tight)', minWidth: 0 }}>{title}</h1>
        {count != null && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-subtle)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '2px 7px' }}>{count}</span>
        )}
        {sub && <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{sub}</span>}
        {actions && <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>{actions}</div>}
      </div>
      {tabsNode}
    </div>
  )
}

export function EmptyState({ icon = 'inbox', title, text, action }: { icon?: string; title: ReactNode; text?: string; action?: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '56px 24px', textAlign: 'center' }}>
      <span style={{ display: 'grid', placeItems: 'center', width: 44, height: 44, borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', color: 'var(--text-subtle)' }}>
        <Icon name={icon} size={20} />
      </span>
      <div style={{ font: 'var(--font-card-title)', color: 'var(--text-body)' }}>{title}</div>
      {text && <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)', maxWidth: 360 }}>{text}</div>}
      {action && <div style={{ marginTop: 6 }}>{action}</div>}
    </div>
  )
}

export function Stat({ label, value, delta, icon, accent }: { label: string; value: ReactNode; delta?: string; icon?: string; accent?: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: '14px 16px',
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        borderTop: accent ? '2px solid var(--accent)' : '1px solid var(--border-default)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, color: 'var(--text-subtle)' }}>
        {icon && <Icon name={icon} size={15} />}
        <span style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>{label}</span>
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 28, fontWeight: 600, color: 'var(--text-strong)', letterSpacing: '-.02em', fontFeatureSettings: '"tnum" 1' }}>{value}</div>
      {delta && <div style={{ marginTop: 6, font: 'var(--font-caption)', color: 'var(--text-muted)' }}>{delta}</div>}
    </div>
  )
}


const LIFE: Record<LifeStatus, { tone: Tone; ru: string }> = {
  planned: { tone: 'neutral', ru: 'Запланирован' },
  received: { tone: 'info', ru: 'Получен' },
  review: { tone: 'accent', ru: 'На рассмотрении' },
  crs: { tone: 'warning', ru: 'Замечания CRS' },
  reissue: { tone: 'danger', ru: 'Переиздание' },
  /* Код B означает «принято с замечаниями»: работать по нему можно, но
     замечания учитываются следующей версией, и круг ещё не окончен. */
  approved: { tone: 'info', ru: 'Принят с замечаниями' },
  /* Код A на подаче в IFR/IFA (V97): принят, но схема назначений ждёт
     чистовой подачи в AFD — круг документа ещё не закрыт. */
  accepted: { tone: 'accent', ru: 'Принят · ожидается AFD' },
  /* «Согласован» — финал схемы назначений: код приёмки на подаче в AFD.
     Версии больше не щёлкаются, продолжение — только новой ревизией. */
  afd: { tone: 'success', ru: 'Согласован' },
  hold: { tone: 'warning', ru: 'Приостановлен' },
  superseded: { tone: 'neutral', ru: 'Заменён' },
  void: { tone: 'neutral', ru: 'Аннулирован' },
}
/* Название состояния — то же, что печатает бейдж на экране. Выгрузка реестра и
   опись комплекта ИД писали внутренний код (review, afd, planned): файл уходит
   ГИПу, заказчику и в приёмочную комиссию, и латиница в колонке статуса делает
   его непригодным без ручной замены. Неизвестный код отдаётся как есть — врать
   о нём нечем. */
export function lifeLabel(life: string): string {
  return LIFE[life as LifeStatus]?.ru ?? life
}

export function LifeBadge({ life }: { life: LifeStatus }) {
  const l = LIFE[life]
  return (
    <Badge tone={l.tone} dot size="sm">
      {l.ru}
    </Badge>
  )
}

export function Purpose({ value, size = 'md' }: { value: PurposeT | '—'; size?: 'sm' | 'md' }) {
  if (!value || value === '—') return <span style={{ color: 'var(--text-subtle)' }}>—</span>
  if (value === 'IFI') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          // Та же защита от растяжения сеткой, что у StatusBadge.
          width: 'fit-content',
          height: size === 'sm' ? 18 : 20,
          padding: '0 8px',
          fontFamily: 'var(--font-mono)',
          fontSize: size === 'sm' ? '10px' : 'var(--text-2xs)',
          fontWeight: 600,
          letterSpacing: '.04em',
          color: 'var(--violet-600)',
          background: 'var(--violet-50)',
          border: '1px solid var(--violet-100)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        IFI
      </span>
    )
  }
  return <StatusBadge status={value} size={size} />
}
